import type { ShopperAiTagId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { ConversationSort } from "@redotech/effect-api-shared/schema/query-options";
import {
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import ArrowDown from "@redotech/redo-icons/arrow-down.svg";
import ArrowUp from "@redotech/redo-icons/arrow-up.svg";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import type { AdvancedFilterData } from "@redotech/redo-model/views/views";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  type CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { deepCopyObject } from "@redotech/util/object";
import { useLiveQuery } from "@tanstack/react-db";
import type { ColumnDef } from "@tanstack/react-table";
import { memo, type MouseEvent, useCallback, useMemo, useRef } from "react";
import {
  type BadgeCellItem,
  BadgeOverflowCell,
} from "../../../return/returns-table-tags-cell";
import { pillThemeToBadgeColor } from "../../../return/tags/tag-colors";
import {
  ChannelBadge,
  StatusBadge,
} from "../../components/badges/support-ticket-badges";
import {
  useAddFilter,
  useAdvancedFilters,
  useColumnSort,
} from "../../filters/filters-context";
import { isSupportTicketUnread } from "../../hooks/use-support-ticket-card";
import { useGlobalSupportTicketCollections } from "../../providers/global-support-ticket-collections-context";
import { EditableAssigneeCell } from "../components/editable-cells/editable-assignee-cell";
import { SupportTicketActionsMenu } from "../components/editable-cells/support-ticket-actions-menu";
import { useSupportTicketsTableContext } from "../support-tickets-table-context";
import type { ConversationColumnMeta } from "../types";
import * as styles from "./support-tickets-columns.module.css";

const ColumnHeader = memo(function ColumnHeader({ title }: { title: string }) {
  return (
    <Text fontSize="xxs" fontWeight="medium" textColor="tertiary">
      {title}
    </Text>
  );
});

const SortableColumnHeader = memo(function SortableColumnHeader({
  sortKind,
  title,
}: {
  sortKind: ConversationSort["kind"];
  title: string;
}) {
  const { sortDirection, toggleSort } = useColumnSort(sortKind);

  return (
    <Flex
      align="center"
      className={styles.clickableHeaderContent}
      cursor="pointer"
      gap="xs"
      onClick={toggleSort}
    >
      <Text fontSize="xxs" fontWeight="medium" textColor="tertiary">
        {title}
      </Text>
      {sortDirection !== null && (
        <Flex align="center" className="h-4 w-4">
          {sortDirection === "desc" ? <ArrowDown /> : <ArrowUp />}
        </Flex>
      )}
    </Flex>
  );
});

const FilterableColumnHeader = memo(function FilterableColumnHeader({
  title,
  filterName,
}: {
  title: string;
  filterName: SupportTicketFilterNames;
}) {
  const addFilter = useAddFilter();
  const { filters } = useAdvancedFilters();

  const handleClick = useCallback(() => {
    const alreadyExists = filters.some((f) => f.name === filterName);
    if (alreadyExists) {
      return;
    }
    const filter = deepCopyObject(
      supportTicketFilterData[filterName],
    ) as AdvancedFilterData;
    addFilter(filter);
  }, [addFilter, filters, filterName]);

  return (
    <Flex
      align="center"
      className={styles.clickableHeaderContent}
      cursor="pointer"
      gap="xs"
      onClick={handleClick}
    >
      <Text fontSize="xxs" fontWeight="medium" textColor="tertiary">
        {title}
      </Text>
    </Flex>
  );
});

function formatDateWithTime(date: Date): { date: string; time: string } {
  const dateStr = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const timeStr = date
    .toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toUpperCase();
  return { date: dateStr, time: timeStr };
}

const ReadColumnHeader = memo(function ReadColumnHeader() {
  const { data, selectedSupportTicketCount, setAllSupportTicketsSelected } =
    useSupportTicketsTableContext();

  const checkboxValue: CheckboxValue =
    selectedSupportTicketCount === 0
      ? false
      : selectedSupportTicketCount === data.length
        ? true
        : "indeterminate";

  const handleSetValue = useCallback(
    (value: CheckboxValue) => {
      setAllSupportTicketsSelected(value === true || value === "indeterminate");
    },
    [setAllSupportTicketsSelected],
  );

  return (
    <Flex align="center" justify="center" pl="sm">
      <RedoCheckbox
        disabled={data.length === 0}
        setValue={handleSetValue}
        size="xs"
        value={checkboxValue}
      />
    </Flex>
  );
});

const ReadColumnCell = memo(function ReadColumnCell({
  conversation,
  rowIndex,
}: {
  conversation: SupportTicketListItemResponse;
  rowIndex: number;
}) {
  const {
    hoveredSupportTicketId,
    hasAnySelectedSupportTickets,
    isSupportTicketSelected,
    setSupportTicketSelected,
  } = useSupportTicketsTableContext();
  const shiftKeyRef = useRef(false);

  const shouldShowCheckbox = useMemo(
    () =>
      hasAnySelectedSupportTickets ||
      hoveredSupportTicketId === conversation.id,
    [hasAnySelectedSupportTickets, hoveredSupportTicketId, conversation.id],
  );

  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleInputClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    shiftKeyRef.current = event.shiftKey;
    event.stopPropagation();
  }, []);

  const handleSetValue = useCallback(
    (value: CheckboxValue) => {
      setSupportTicketSelected({
        supportTicketId: conversation.id,
        rowIndex,
        checked: value === true || value === "indeterminate",
        shiftKey: shiftKeyRef.current,
      });
      shiftKeyRef.current = false;
    },
    [setSupportTicketSelected, conversation.id, rowIndex],
  );

  if (shouldShowCheckbox) {
    return (
      <Flex
        align="center"
        className={styles.readCellContainer}
        justify="center"
        pl="sm"
      >
        <div onClick={handleClick} onMouseDown={handleClick}>
          <RedoCheckbox
            onClick={handleInputClick}
            setValue={handleSetValue}
            size="xs"
            value={isSupportTicketSelected(conversation.id)}
          />
        </div>
      </Flex>
    );
  }

  if (isSupportTicketUnread(conversation)) {
    return (
      <Flex
        align="center"
        className={styles.readCellContainer}
        justify="center"
      >
        <div className={styles.unreadIndicator} />
      </Flex>
    );
  }

  return null;
});

const TagsCell = memo(function TagsCell({
  tagIds,
}: {
  tagIds: readonly ShopperAiTagId[];
}) {
  const collections = useGlobalSupportTicketCollections();
  const { data: tags } = useLiveQuery(
    (q) => q.from({ tag: collections.tags }),
    [],
  );
  const currentTags = tags.filter((tag) => tagIds.includes(tag.id));

  const badges: BadgeCellItem[] = currentTags.map((tag) => ({
    text: tag.name,
    color: pillThemeToBadgeColor[tag.pillTheme ?? PillTheme.NORMAL],
  }));

  return <BadgeOverflowCell badges={badges} emptyText="" />;
});

const SummaryCell = memo(function SummaryCell({
  summary,
}: {
  summary: string | null;
}) {
  return (
    <Text
      fontSize="xs"
      fontStyle={summary ? undefined : "italic"}
      overflow="hidden"
      textColor="tertiary"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {summary ?? "No generated summary"}
    </Text>
  );
});

/**
 * Get column definitions for conversations table
 * Column order:
 * 1. Read indicator (dot if unread)
 * 2. Ticket #
 * 3. Status
 * 4. Customer
 * 5. Summary
 * 6. Priority
 * 7. Channel
 * 8. Assignee
 * 9. Last response
 * 10. Created at
 * 11. Actions (3-dot menu)
 */
export function getSupportTicketsTableColumns(_options?: {
  enablePinning?: boolean;
}): ColumnDef<SupportTicketListItemResponse, unknown>[] {
  return [
    {
      id: "read",
      accessorKey: "readAt",
      header: () => <ReadColumnHeader />,
      cell: ({ row }) => (
        <ReadColumnCell conversation={row.original} rowIndex={row.index} />
      ),
      meta: { label: "Read", variant: "text" } satisfies ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      enableResizing: false,
      size: 24,
      minSize: 24,
    },
    {
      id: "ticketNumber",
      accessorKey: "ticketNumber",
      header: () => <ColumnHeader title="Ticket #" />,
      cell: ({ row }) => {
        const ticketNumber = row.original.ticketNumber;
        const ticketDisplay = ticketNumber != null ? `#${ticketNumber}` : "";
        return (
          <Text
            fontSize="xs"
            fontWeight={
              isSupportTicketUnread(row.original) ? "semibold" : "regular"
            }
            textColor="primary"
          >
            {ticketDisplay}
          </Text>
        );
      },
      meta: {
        label: "Ticket #",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 80,
    },
    {
      id: "status",
      accessorKey: "status",
      header: () => <ColumnHeader title="Status" />,
      cell: ({ row }) => {
        return <StatusBadge size="sm" status={row.original.status} />;
      },
      meta: {
        label: "Status",
        variant: "badge",
        headerClassName: styles.headerCellHover,
      } satisfies ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: true,
      filterFn: (row, id, value: string[]) => {
        const cellValue = row.getValue(id);
        return value.includes(String(cellValue));
      },
      size: 140,
    },
    {
      id: "customer",
      accessorKey: "mongoCustomerId",
      header: () => <ColumnHeader title="Customer" />,
      cell: ({ row }) => {
        const customer = row.original.customer;
        return (
          <Text
            fontSize="xs"
            fontWeight={
              isSupportTicketUnread(row.original) ? "semibold" : "regular"
            }
            textColor="primary"
          >
            {customer?.displayName ?? "Unknown Customer"}
          </Text>
        );
      },
      meta: {
        label: "Customer",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 160,
    },
    {
      id: "summary",
      accessorKey: "summary",
      header: () => <ColumnHeader title="Summary" />,
      cell: ({ row }) => <SummaryCell summary={row.original.summary} />,
      meta: {
        label: "Summary",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 200,
    },
    {
      id: "platform",
      accessorKey: "channels",
      header: () => (
        <FilterableColumnHeader
          filterName={SupportTicketFilterNames.CHANNELS}
          title="Channel"
        />
      ),
      cell: ({ row }) => {
        const channels = row.original.channels;
        if (!channels || channels.length === 0) {
          return null;
        }
        const firstChannel = channels[0];
        if (!firstChannel) {
          return null;
        }
        return <ChannelBadge channel={firstChannel} size="sm" />;
      },
      meta: {
        label: "Channel",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 120,
    },
    {
      id: "priority",
      accessorKey: "priority",
      header: () => <ColumnHeader title="Priority" />,
      cell: ({ row }) => {
        const priority = row.original.priority;
        if (priority !== "URGENT") {
          return null;
        }
        return <RedoBadge color="error" size="sm" text="Urgent" />;
      },
      meta: {
        label: "Priority",
        variant: "badge",
        headerClassName: styles.headerCellHover,
      } satisfies ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, id, value: string[]) => {
        const cellValue = row.getValue(id);
        if (!cellValue) {
          return false;
        }
        return value.includes(String(cellValue));
      },
      size: 120,
    },
    {
      id: "assignee",
      header: () => (
        <FilterableColumnHeader
          filterName={SupportTicketFilterNames.ASSIGNEES}
          title="Assignee"
        />
      ),
      cell: ({ row }) => {
        return <EditableAssigneeCell conversation={row.original} />;
      },
      meta: {
        label: "Assignee",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 100,
    },
    {
      id: "lastResponse",
      accessorKey: "lastCustomerMessageAt",
      header: () => (
        <SortableColumnHeader
          sortKind="lastCustomerMessageAt"
          title="Last response"
        />
      ),
      cell: ({ row }) => {
        const date = row.original.lastCustomerMessageAt;
        if (!date) {
          return null;
        }
        const formatted = formatDateWithTime(date);
        return (
          <Flex align="center" gap="xs">
            <Text fontSize="xs" fontWeight="regular" textColor="primary">
              {formatted.date}
            </Text>
            <Text fontSize="xs" textColor="tertiary">
              {formatted.time}
            </Text>
          </Flex>
        );
      },
      meta: {
        label: "Last response",
        variant: "date",
        headerClassName: styles.headerCellHover,
      } satisfies ConversationColumnMeta,
      enableSorting: true,
      enableColumnFilter: false,
      size: 130,
    },
    {
      id: "tags",
      header: () => (
        <FilterableColumnHeader
          filterName={SupportTicketFilterNames.CONVERSATION_TAGS}
          title="Tags"
        />
      ),
      cell: ({ row }) => <TagsCell tagIds={row.original.tagIds} />,
      meta: {
        label: "Tags",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 200,
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => (
        <SupportTicketActionsMenu conversation={row.original} />
      ),
      meta: {
        label: "Actions",
        variant: "text",
      } satisfies ConversationColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      enableResizing: false,
      size: 48,
      minSize: 48,
    },
  ];
}
